import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

/** Just enough to fill a field and, where the screen knows it, label the button. */
export interface CurrentProject {
  id: string;
  /** Absent where the screen has the id but not the name — a sprint carries only the id. */
  name?: string;
}

interface Store {
  project: CurrentProject | null;
  set: (project: CurrentProject | null) => void;
}

const Context = createContext<Store | null>(null);

/**
 * Which project the reader is looking at, published by the screen that knows.
 *
 * <p>The shell's create button needs this and cannot work it out for itself: `/sprints/:id` and
 * `/work-items/:id` carry no project in the path, and the page below has already fetched it. So
 * the page tells the shell rather than the shell parsing the URL and fetching a second time.
 *
 * <p>Deliberately not persisted. It describes where you are, and a stale answer to that is worse
 * than no answer — it would file work against a project you left ten minutes ago.
 */
export function CurrentProjectProvider({ children }: { children: ReactNode }) {
  const [project, set] = useState<CurrentProject | null>(null);
  const value = useMemo(() => ({ project, set }), [project]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCurrentProject(): CurrentProject | null {
  return useContext(Context)?.project ?? null;
}

/**
 * Publishes the project this screen is about, for as long as it is on screen.
 *
 * <p>Cleared on unmount. Navigating away from a project must not leave the shell believing you
 * are still in it — React unmounts the old route before mounting the new one, so a screen that
 * publishes its own project overwrites this immediately and one that does not leaves it empty,
 * which is the honest answer.
 *
 * <p>Takes id and name separately rather than an object so callers can pass fields straight from
 * a query result without memoising a wrapper on every render.
 */
export function usePublishCurrentProject(id: string | undefined, name?: string): void {
  const store = useContext(Context);

  useEffect(() => {
    if (!store || !id) {
      return;
    }
    store.set({ id, name });
    return () => store.set(null);
    // `store.set` is a stable setter; depending on `store` itself would re-run on every change
    // of the very value this writes, which is a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name]);
}
